/**
 * ReviewQueueState — the navigation snapshot the Tablero's expediente browser
 * hands to the detail view via `location.state`.
 *
 * The snapshot is intentionally FROZEN: the browser sorts the list by the
 * effective decision (HUMAN_REVIEW → REJECTED → APPROVED, then id), so
 * adjudicating an expediente reshuffles a refetched list mid-session. The
 * reviewer works through exactly the page they saw; prev/next never refetch.
 *
 * `location.state` is untyped and survives history navigation, so consumers
 * must validate with `isReviewQueueState` and check `roundId` against the
 * route before trusting it. Cold deep-links carry no state — the detail view
 * degrades to a plain back link.
 */

import type { Decision } from "../../api/client";
import { serializeTableroView } from "../roundIndex/urlState";

export type ReviewQueueState = {
  /** The visible page's expediente_ids, in browser order (≤ page_size). */
  ids: string[];
  /** Position of the current expediente within `ids`. */
  index: number;
  /** 1-indexed page in the browser at snapshot time. */
  page: number;
  pageSize: number;
  /** Total matching expedientes across all pages (for "N de M"). */
  total: number;
  /** The browser view at snapshot time — verdict, motivo, search (#333) and
   * the page (#336) — restored on the way back to the Tablero. */
  filters: { decision: "ALL" | Decision; reason: string; q?: string; page?: number };
  /** Guard: ignore the snapshot if it doesn't match the route's round. */
  roundId: string;
};

/** Defensive shape check for the untyped `location.state.queue` value. */
export function isReviewQueueState(value: unknown): value is ReviewQueueState {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    Array.isArray(v.ids) &&
    v.ids.every((id) => typeof id === "string") &&
    typeof v.index === "number" &&
    Number.isInteger(v.index) &&
    v.index >= 0 &&
    v.index < v.ids.length &&
    typeof v.page === "number" &&
    typeof v.pageSize === "number" &&
    typeof v.total === "number" &&
    typeof v.roundId === "string" &&
    typeof v.filters === "object" &&
    v.filters !== null
  );
}

/** The Tablero URL to go back to: the round plus the view the snapshot was
 * taken under (verdict, motivo, search, page), so the reviewer returns to the
 * results they were walking. */
export function tableroPath(roundId: string, queue: ReviewQueueState | null): string {
  if (!queue) return `/rounds/${roundId}`;
  const { decision, reason, q = "", page = queue.page } = queue.filters;
  const search = serializeTableroView({ decision, reason, q, page }).toString();
  return search ? `/rounds/${roundId}?${search}` : `/rounds/${roundId}`;
}
